import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { ArrowRight, MapPin } from 'react-feather';
import { ISearchRecord } from '../types/search';
import { IAuthContext } from '../context/auth/auth-context';
import { searchFavorite } from '../services/firebase';
import { buildMyTundraPath } from '../util/myTundraNavigation.util';
import {
  HOMEPAGE_FACILITY_SLOT_COUNT,
  isValidLatLon,
  mapFavoritesPayload,
} from '../util/favoriteApi.util';
import { resolveRecordLocation } from '../util/entityLocation.util';
import {
  MANAGE_CARD_ARIA_LABEL,
  MANAGE_CARD_SUBTEXT,
  MANAGE_CARD_TITLE,
} from '../util/facilityHomepageCopy';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SectionOuter = styled.div<{
  $placement: 'below-search' | 'page-bottom';
  $clearEsiOverlap?: boolean;
}>`
  width: 100%;
  max-width: 720px;
  margin-top: ${(p) => (p.$placement === 'page-bottom' ? 'auto' : '0')};
  flex-shrink: 0;
  padding-bottom: ${(p) => (p.$placement === 'page-bottom' && !p.$clearEsiOverlap ? 'clamp(1rem, 3vh, 5.5rem)' : '0')};

  ${(p) =>
    p.$placement === 'page-bottom' &&
    p.$clearEsiOverlap &&
    css`
      margin-top: 0;
      max-width: min(1040px, calc(100vw - 680px));
      text-align: left;

      @media (max-width: 1100px) {
        max-width: min(720px, calc(100vw - 420px));
      }

      @media (max-width: 900px) {
        max-width: 100%;
        padding-bottom: clamp(7rem, 16vh, 9rem);
      }
    `}

  @media (max-width: 900px) {
    padding-bottom: ${(p) => {
      if (p.$clearEsiOverlap) return 'clamp(7rem, 16vh, 9rem)';
      if (p.$placement === 'page-bottom') return '6.5rem';
      return '0';
    }};
  }
`;

const Section = styled.section<{ $placement: 'below-search' | 'page-bottom' }>`
  width: 100%;
  max-width: 720px;
  margin-top: ${(p) => (p.$placement === 'below-search' ? '20px' : '0')};
  animation: fadeIn 0.35s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  margin-bottom: 10px;
  border-radius: 12px;
  background: rgba(8, 12, 18, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
`;

const SectionTitle = styled.span`
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SiteCard = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  background: rgba(8, 12, 18, 0.78);
  transition: border-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
  font-family: inherit;

  &:hover {
    border-color: rgba(52, 211, 153, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
`;

const MapPreview = styled.div`
  height: 108px;
  width: 100%;
  background-color: #0f172a;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.55) 100%);
    pointer-events: none;
    z-index: 1;
  }
`;

const MapPlaceholder = styled.div`
  height: 108px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.85));
  color: #64748b;
`;

const CardTitle = styled.div`
  padding: 10px 12px 12px;
  color: #f1f5f9;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MoreCard = styled.button`
  appearance: none;
  border: 1px dashed rgba(52, 211, 153, 0.35);
  border-radius: 14px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  text-align: center;
  background: rgba(8, 12, 18, 0.65);
  transition: border-color 0.15s ease, transform 0.12s ease, background 0.15s ease;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  min-height: 100%;

  &:hover {
    border-color: rgba(52, 211, 153, 0.65);
    background: rgba(12, 20, 28, 0.85);
    transform: translateY(-2px);

    svg {
      transform: translateX(3px);
    }
  }
`;

const MoreCardPreview = styled.div`
  height: 108px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.9), rgba(6, 78, 59, 0.25));
  color: #34d399;

  svg {
    transition: transform 0.15s ease;
  }
`;

const MoreCardBody = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MoreCardTitle = styled.span`
  color: #f1f5f9;
  font-size: 13px;
  font-weight: 600;
`;

const MoreCardSub = styled.span`
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
`;

const SkeletonCard = styled.div`
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(8, 12, 18, 0.6);
`;

const SkeletonMap = styled.div`
  height: 108px;
  background: linear-gradient(90deg, rgba(30, 41, 59, 0.5) 25%, rgba(51, 65, 85, 0.55) 50%, rgba(30, 41, 59, 0.5) 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

const SkeletonTitle = styled.div`
  height: 14px;
  margin: 12px;
  border-radius: 6px;
  width: 70%;
  background: linear-gradient(90deg, rgba(30, 41, 59, 0.5) 25%, rgba(51, 65, 85, 0.55) 50%, rgba(30, 41, 59, 0.5) 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY as string | undefined;

const buildStaticMapUrl = (lat: number, lon: number): string => {
  const params = new URLSearchParams({
    center: `${lat},${lon}`,
    zoom: '16',
    size: '320x108',
    scale: '2',
    maptype: 'satellite',
    markers: `color:0xef4444|${lat},${lon}`,
    key: GOOGLE_MAP_KEY as string,
  });

  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
};

const STATIC_MAP_MAX_RETRIES = 2;

const FavoriteMapPreview: React.FC<{ lat?: number; lon?: number }> = ({ lat, lon }) => {
  const baseUrl = useMemo(
    () => (isValidLatLon(lat, lon) ? buildStaticMapUrl(lat!, lon!) : null),
    [lat, lon]
  );

  // `attempt` is bumped on error to retry with a cache-busting param so the
  // browser re-issues the request instead of reusing the cached 403.
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  // Reset retry state if the coordinates change (card re-used for another site).
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [baseUrl]);

  if (!GOOGLE_MAP_KEY || !baseUrl || failed) {
    return (
      <MapPlaceholder aria-hidden>
        <MapPin size={22} />
      </MapPlaceholder>
    );
  }

  const src = attempt === 0 ? baseUrl : `${baseUrl}&retry=${attempt}`;

  return (
    <MapPreview>
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => {
          if (attempt < STATIC_MAP_MAX_RETRIES) {
            // Stagger retries so a quota burst has time to clear.
            window.setTimeout(() => setAttempt((a) => a + 1), 400 * (attempt + 1));
          } else {
            setFailed(true);
          }
        }}
        style={{ width: '100%', height: 108, objectFit: 'cover', display: 'block' }}
      />
    </MapPreview>
  );
};

const getSiteLabel = (favorite: ISearchRecord): string =>
  favorite.entityName || favorite.entityNumber || favorite.displayName || 'Saved site';

interface RecentFavoriteSitesProps {
  context: IAuthContext;
  maxItems?: number;
  /** `page-bottom` pins the row to the lower hero area (Enviro Chat). */
  placement?: 'below-search' | 'page-bottom';
  /** Keep cards left of the fixed "Climb to ESI" timeline (Enviro Chat). */
  clearEsiOverlap?: boolean;
}

const RecentFavoriteSites: React.FC<RecentFavoriteSitesProps> = ({
  context,
  maxItems = HOMEPAGE_FACILITY_SLOT_COUNT,
  placement = 'below-search',
  clearEsiOverlap = false,
}) => {
  const history = useHistory();

  const [favorites, setFavorites] = useState<ISearchRecord[]>([]);
  const [totalFavoriteCount, setTotalFavoriteCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchDone, setFetchDone] = useState(false);

  useEffect(() => {
    if (!context.isAuthenticated || !context.isInitialized) {
      setFavorites([]);
      setTotalFavoriteCount(0);
      setFetchDone(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setFetchDone(false);
      try {
        const result = await searchFavorite({
          type: 'getAll',
          pageSize: maxItems,
          lastItemIndex: 0,
          previewOnly: true,
        });
        const payload = result.data as
          | { data?: Record<string, unknown>[]; totalItems?: number }
          | undefined;
        const { list, totalItems } = mapFavoritesPayload(payload, true);

        if (cancelled) return;

        const previewList = list.slice(0, maxItems);
        // Resolve every card's coords before revealing the row so map
        // thumbnails appear together (avoids one map + two placeholders).
        const resolved = await Promise.all(
          previewList.map((favorite) =>
            isValidLatLon(favorite.location?.lat, favorite.location?.lon)
              ? Promise.resolve(favorite)
              : resolveRecordLocation(favorite)
          )
        );
        if (cancelled) return;

        setTotalFavoriteCount(totalItems);
        setFavorites(resolved);
      } catch {
        if (!cancelled) {
          setFavorites([]);
          setTotalFavoriteCount(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setFetchDone(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.isAuthenticated, context.isInitialized, maxItems]);

  const topFavorites = useMemo(() => favorites.slice(0, maxItems), [favorites, maxItems]);

  const sectionLabel = 'My Facilities';

  const openFavorite = (favorite: ISearchRecord) => {
    sessionStorage.setItem(
      'myTundraPendingFavorite',
      JSON.stringify({
        contentId: favorite.contentId,
        entityNumber: favorite.entityNumber,
        record: favorite,
      })
    );
    history.push(
      buildMyTundraPath({
        contentId: favorite.contentId,
        entityNumber: favorite.entityNumber,
      })
    );
  };

  const openMyEnvironment = () => {
    sessionStorage.setItem('myTundraLoadAllFavorites', '1');
    history.push('/my-tundra');
  };

  const remainingCount = Math.max(0, totalFavoriteCount - maxItems);
  const moreSubtext =
    remainingCount > 0
      ? `${remainingCount} more saved ${remainingCount === 1 ? 'facility' : 'facilities'}`
      : MANAGE_CARD_SUBTEXT;

  if (!context.isAuthenticated || !context.isInitialized) return null;

  if (isLoading || !fetchDone) {
    return (
      <SectionOuter $placement={placement} $clearEsiOverlap={clearEsiOverlap}>
        <Section $placement={placement} aria-label="Loading recent favorites">
          <SectionHeader>
            <SectionTitle>{sectionLabel}</SectionTitle>
          </SectionHeader>
          <CardsRow>
            {Array.from({ length: maxItems }).map((_, i) => (
              <SkeletonCard key={i}>
                <SkeletonMap />
                <SkeletonTitle />
              </SkeletonCard>
            ))}
            <SkeletonCard aria-hidden>
              <SkeletonMap />
              <SkeletonTitle />
            </SkeletonCard>
          </CardsRow>
        </Section>
      </SectionOuter>
    );
  }

  if (!topFavorites.length) return null;

  return (
    <SectionOuter $placement={placement} $clearEsiOverlap={clearEsiOverlap}>
      <Section $placement={placement} aria-label="AI Earth favorite sites">
        <SectionHeader>
          <SectionTitle>{sectionLabel}</SectionTitle>
        </SectionHeader>
        <CardsRow>
          {topFavorites.map((favorite, index) => {
            const key = favorite.contentId || favorite.entityNumber || `fav-${index}`;
            const lat = favorite.location?.lat;
            const lon = favorite.location?.lon;

            return (
              <SiteCard key={key} type="button" onClick={() => openFavorite(favorite)}>
                <FavoriteMapPreview lat={lat} lon={lon} />
                <CardTitle>{getSiteLabel(favorite)}</CardTitle>
              </SiteCard>
            );
          })}
          <MoreCard type="button" onClick={openMyEnvironment} aria-label={MANAGE_CARD_ARIA_LABEL}>
            <MoreCardPreview>
              <ArrowRight size={28} strokeWidth={2} />
            </MoreCardPreview>
            <MoreCardBody>
              <MoreCardTitle>{MANAGE_CARD_TITLE}</MoreCardTitle>
              <MoreCardSub>{moreSubtext}</MoreCardSub>
            </MoreCardBody>
          </MoreCard>
        </CardsRow>
      </Section>
    </SectionOuter>
  );
};

export default RecentFavoriteSites;
